// src/hooks/usePushNotifications.ts — Bootstrap FCM PWA (opt-in)

import { useCallback, useEffect, useMemo, useState } from 'react';
import { message } from 'antd';
import { useAuth } from '../contexts/AuthContext';
import {
  getNotificationPermission,
  registerWebPushDevice,
  refreshPushTokenLastSeen,
  disableCurrentPushDevice,
} from '../services/pushDeviceService';
import { getFirebaseVapidKey } from '../config/firebase';
import { useAppTranslation } from './useAppTranslation';

export type PushRegistrationStatus =
  | 'idle'
  | 'unsupported'
  | 'missing_vapid'
  | 'default'
  | 'granted'
  | 'denied'
  | 'registering'
  | 'registered'
  | 'error';

/**
 * Gère la permission + enregistrement token.
 * Ne demande **pas** la permission au mount — appeler `enablePush()` (CTA).
 * Si déjà `granted`, rafraîchit silencieusement le token.
 */
export function usePushNotifications() {
  const { t } = useAppTranslation('notifications');
  const { user, userProfile, currentBusiness } = useAuth();
  const [status, setStatus] = useState<PushRegistrationStatus>('idle');
  const [permission, setPermission] = useState<NotificationPermission>('default');
  const [errorReason, setErrorReason] = useState<string | undefined>();

  const businessIds = userProfile?.businessIds ?? [];
  const uid = user?.uid;
  const vapidConfigured = Boolean(getFirebaseVapidKey());

  const resolvedBusinessIds = useMemo(() => {
    if (currentBusiness?.id) {
      return Array.from(new Set([...businessIds, currentBusiness.id]));
    }
    return businessIds;
  }, [businessIds, currentBusiness?.id]);

  const syncIfGranted = useCallback(async () => {
    if (!uid) return;
    if (!getFirebaseVapidKey()) {
      setStatus('missing_vapid');
      return;
    }
    if (typeof window === 'undefined' || !('Notification' in window)) {
      setStatus('unsupported');
      return;
    }
    const currentPermission = await getNotificationPermission();
    setPermission(currentPermission);
    if (currentPermission === 'granted') {
      setStatus('registering');
      await refreshPushTokenLastSeen({
        uid,
        businessIds: resolvedBusinessIds,
      });
      setStatus('registered');
      return;
    }
    if (currentPermission === 'denied') {
      setStatus('denied');
      return;
    }
    setStatus('default');
  }, [uid, resolvedBusinessIds]);

  useEffect(() => {
    void syncIfGranted();
  }, [syncIfGranted]);

  const enablePush = useCallback(async () => {
    if (!uid) return { ok: false as const, reason: 'unauthenticated' };
    if (!getFirebaseVapidKey()) {
      setStatus('missing_vapid');
      message.warning(t('push.missingVapid'));
      return { ok: false as const, reason: 'missing_vapid' };
    }
    setStatus('registering');
    setErrorReason(undefined);
    const result = await registerWebPushDevice({
      uid,
      businessIds: resolvedBusinessIds,
    });
    if (typeof window !== 'undefined' && 'Notification' in window) {
      setPermission(Notification.permission);
    }
    if (result.ok) {
      setStatus('registered');
      message.success(t('push.enabled'));
    } else {
      setErrorReason(result.reason);
      if (result.reason === 'denied') {
        setStatus('denied');
        message.warning(t('push.denied'));
      } else if (result.reason === 'unsupported' || result.reason === 'messaging_unsupported') {
        setStatus('unsupported');
        message.warning(t('push.unsupported'));
      } else if (result.reason === 'missing_vapid') {
        setStatus('missing_vapid');
        message.warning(t('push.missingVapid'));
      } else {
        setStatus('error');
        message.error(t('push.error'));
      }
    }
    return result;
  }, [uid, resolvedBusinessIds, t]);

  const disablePush = useCallback(async () => {
    if (!uid) return;
    await disableCurrentPushDevice(uid);
    setStatus('default');
  }, [uid]);

  const isSupported =
    typeof window !== 'undefined' &&
    'Notification' in window &&
    'serviceWorker' in navigator;

  return {
    status,
    permission,
    errorReason,
    enablePush,
    disablePush,
    refresh: syncIfGranted,
    isSupported,
    vapidConfigured,
    isRegistered: status === 'registered',
    isRegistering: status === 'registering',
    isPushActive: status === 'registered',
  };
}
